import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Spinner,
  Row,
  Col,
  Label,
  Input,
  FormGroup,
  Table,
} from "reactstrap";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useToasts } from "helper/ToastAdapter";
import { fetchApi } from "redux/action/api_tbeams";
import { apiURL } from "redux/action/api_tbeams";
import moment from "moment";

export default function BudgetEntriesReport() {
  const { addToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [mdaList, setMdaList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [form, setForm] = useState({
    budget_year: moment().format("YYYY"),
    mda_code: "",
    report_type: "revenue",
  });

  // Fetch MDA list for dropdown
  useEffect(() => {
    fetchApi(`get-budget-summary?query_type=list2`)
      .then((d) => {
        if (d.result) {
          setMdaList(d.result);
        }
      })
      .catch((err) => console.log(err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Reset report data when filters change
    setReportData(null);
  };

  const toggleRow = (accountCode) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(accountCode)) {
        newSet.delete(accountCode);
      } else {
        newSet.add(accountCode);
      }
      return newSet;
    });
  };

  const handleGenerateReport = async () => {
    if (!form.budget_year) {
      addToast("Please select a budget year", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
      return;
    }

    setIsLoading(true);

    try {
      // Build query parameters
      const params = new URLSearchParams({
        budget_year: form.budget_year,
        report_type: form.report_type,
      });

      if (form.mda_code) {
        params.append("mda_code", form.mda_code);
      }

      // Fetch the JSON data
      const response = await fetch(
        `${apiURL}/excel-reports/budget-data?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to generate report");
      }

      const result = await response.json();

      if (result.success) {
        setReportData(result);
        // Expand top level rows by default
        if (result.data && result.data.length > 0) {
          const topLevelCodes = result.data
            .filter((item) => item.displayLevel === 0)
            .map((item) => item.account_code);
          setExpandedRows(new Set(topLevelCodes));
        }

        addToast("Report generated successfully", {
          appearance: "success",
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
      } else {
        throw new Error(result.message || "Failed to generate report");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      addToast("Failed to generate report. Please try again.", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!form.budget_year) {
      addToast("Please select a budget year", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
      return;
    }

    setIsDownloading(true);

    try {
      // Build query parameters
      const params = new URLSearchParams({
        budget_year: form.budget_year,
        report_type: form.report_type,
      });

      if (form.mda_code) {
        params.append("mda_code", form.mda_code);
      }

      // Fetch the Excel file
      const response = await fetch(
        `${apiURL}/excel-reports/budget-performance?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to generate report");
      }

      // Create a blob from the response
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Budget_Report_${form.budget_year}_${form.report_type}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      addToast("Excel report downloaded successfully", {
        appearance: "success",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
    } catch (error) {
      console.error("Error downloading report:", error);
      addToast("Failed to download report. Please try again.", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const renderHierarchicalRow = (item, index) => {
    const isExpanded = expandedRows.has(item.account_code);
    const indentWidth = item.displayLevel * 25;

    return (
      <tr
        key={item.account_code || index}
        style={{
          backgroundColor: item.hasChildren ? "#f8f9fa" : "white",
          fontWeight: item.hasChildren ? "600" : "normal",
        }}
      >
        <td style={{ paddingLeft: `${indentWidth + 10}px` }}>
          {item.hasChildren && (
            <i
              className={`fas fa-chevron-${isExpanded ? "down" : "right"}`}
              style={{
                marginRight: "8px",
                cursor: "pointer",
                color: "#495057",
              }}
              onClick={() => toggleRow(item.account_code)}
            />
          )}
          {item.account_code}
        </td>
        <td>{item.account_description || item.description}</td>
        <td style={{ textAlign: "center" }}>{item.level || "-"}</td>
        <td style={{ textAlign: "center" }}>{item.type || "-"}</td>
        <td style={{ textAlign: "right" }}>
          {formatCurrency(item.total_credit)}
        </td>
        <td style={{ textAlign: "right" }}>
          {formatCurrency(item.total_debit)}
        </td>
        <td style={{ textAlign: "right", fontWeight: "600" }}>
          {formatCurrency(item.net_amount)}
        </td>
        <td style={{ textAlign: "center" }}>{item.transaction_count || 0}</td>
      </tr>
    );
  };

  const renderFlatRow = (item, index) => {
    return (
      <tr key={index}>
        <td>{item.category || item.mda_code || item.account_code}</td>
        <td>{item.mda_description || item.account_description || "-"}</td>
        <td style={{ textAlign: "right" }}>
          {formatCurrency(
            item.total_amount || item.total_revenue || item.total_credit,
          )}
        </td>
        <td style={{ textAlign: "right" }}>
          {formatCurrency(item.total_expenditure || item.total_debit)}
        </td>
        <td style={{ textAlign: "right", fontWeight: "600" }}>
          {formatCurrency(item.net_position || item.net_amount)}
        </td>
        <td style={{ textAlign: "center" }}>
          {item.mda_count || item.transaction_count || "-"}
        </td>
      </tr>
    );
  };

  const filterDisplayedRows = () => {
    if (!reportData || !reportData.data) return [];

    // For hierarchical reports, filter based on expanded state
    if (
      ["revenue", "expenditure", "economic_analysis"].includes(form.report_type)
    ) {
      const filtered = [];
      let skipUntilLevel = null;

      reportData.data.forEach((item) => {
        // If we're skipping and this item is not at a lower level, continue skipping
        if (skipUntilLevel !== null && item.displayLevel > skipUntilLevel) {
          return;
        }

        // Reset skip state
        skipUntilLevel = null;

        // Add this row
        filtered.push(item);

        // If this row has children but is not expanded, skip children
        if (item.hasChildren && !expandedRows.has(item.account_code)) {
          skipUntilLevel = item.displayLevel;
        }
      });

      return filtered;
    }

    // For non-hierarchical reports, show all rows
    return reportData.data;
  };

  const getReportTitle = () => {
    const titles = {
      revenue: "Revenue Report",
      expenditure: "Expenditure Report",
      summary: "Budget Summary",
      mda_breakdown: "MDA Breakdown Report",
      economic_analysis: "Economic Analysis Report",
    };
    return titles[form.report_type] || "Budget Report";
  };

  const isHierarchicalReport = () => {
    return ["revenue", "expenditure", "economic_analysis"].includes(
      form.report_type,
    );
  };

  return (
    <DashboardLayout>
      <MDBox pt={2} pb={3} pl={2} pr={2}>
        <Card style={{ minHeight: "80vh", padding: "20px" }}>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h5" color="white">
              Budget Entries Report
            </MDTypography>
          </MDBox>

          <MDBox p={3} mt={3}>
            <Row>
              <Col md={12}>
                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "20px",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                >
                  <MDTypography variant="h6" style={{ marginBottom: "15px" }}>
                    Report Information
                  </MDTypography>
                  <p style={{ color: "#666", marginBottom: "10px" }}>
                    This report generates comprehensive budget analysis based on
                    budget entries and account chart data. Select filters and
                    click "Generate Report" to view data with parent/child
                    hierarchy, then download as Excel.
                  </p>
                  <ul style={{ color: "#666", marginLeft: "20px" }}>
                    <li>
                      <strong>Revenue Report:</strong> Credit transactions with
                      account hierarchy
                    </li>
                    <li>
                      <strong>Expenditure Report:</strong> Debit transactions
                      with account hierarchy
                    </li>
                    <li>
                      <strong>Budget Summary:</strong> Overall budget position
                    </li>
                    <li>
                      <strong>MDA Breakdown:</strong> Financial position per MDA
                    </li>
                    <li>
                      <strong>Economic Analysis:</strong> By economic
                      classification
                    </li>
                  </ul>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label style={{ fontWeight: "600", color: "#495057" }}>
                    Budget Year <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    name="budget_year"
                    value={form.budget_year}
                    onChange={handleChange}
                    style={{
                      borderRadius: "6px",
                      border: "1px solid #ced4da",
                      padding: "8px 12px",
                    }}
                  >
                    <option value="">--Select Year--</option>
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = moment().year() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label style={{ fontWeight: "600", color: "#495057" }}>
                    Report Type <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    name="report_type"
                    value={form.report_type}
                    onChange={handleChange}
                    style={{
                      borderRadius: "6px",
                      border: "1px solid #ced4da",
                      padding: "8px 12px",
                    }}
                  >
                    <option value="revenue">Revenue Report</option>
                    <option value="expenditure">Expenditure Report</option>
                    <option value="summary">Budget Summary</option>
                    <option value="mda_breakdown">MDA Breakdown</option>
                    <option value="economic_analysis">Economic Analysis</option>
                  </Input>
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label style={{ fontWeight: "600", color: "#495057" }}>
                    Filter by MDA (Optional)
                  </Label>
                  <Input
                    type="select"
                    name="mda_code"
                    value={form.mda_code}
                    onChange={handleChange}
                    style={{
                      borderRadius: "6px",
                      border: "1px solid #ced4da",
                      padding: "8px 12px",
                    }}
                  >
                    <option value="">All MDAs</option>
                    {mdaList.map((mda, idx) => (
                      <option key={idx} value={mda.mda_code}>
                        {mda.mda_code} - {mda.mda_name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <div style={{ textAlign: "center" }}>
                  <Button
                    color="primary"
                    size="lg"
                    onClick={handleGenerateReport}
                    disabled={isLoading || !form.budget_year}
                    style={{
                      padding: "12px 40px",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      marginRight: "15px",
                    }}
                  >
                    {isLoading ? (
                      <>
                        <Spinner size="sm" style={{ marginRight: "10px" }} />
                        Generating...
                      </>
                    ) : (
                      <>
                        <i
                          className="fas fa-chart-bar"
                          style={{ marginRight: "10px" }}
                        ></i>
                        Generate Report
                      </>
                    )}
                  </Button>

                  {reportData && (
                    <Button
                      color="success"
                      size="lg"
                      onClick={handleDownload}
                      disabled={isDownloading}
                      style={{
                        padding: "12px 40px",
                        fontSize: "16px",
                        fontWeight: "600",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      {isDownloading ? (
                        <>
                          <Spinner size="sm" style={{ marginRight: "10px" }} />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <i
                            className="fas fa-file-excel"
                            style={{ marginRight: "10px" }}
                          ></i>
                          Download Excel
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </Col>
            </Row>

            {/* Display Report Data */}
            {reportData && reportData.data && (
              <Row className="mt-4">
                <Col md={12}>
                  <div
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px",
                      }}
                    >
                      <MDTypography variant="h5" style={{ color: "#344767" }}>
                        {getReportTitle()}
                      </MDTypography>
                      <div style={{ color: "#666", fontSize: "14px" }}>
                        Budget Year: <strong>{reportData.budget_year}</strong> |
                        MDA: <strong>{reportData.mda_code}</strong>
                      </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                      <Table striped bordered hover responsive>
                        <thead
                          style={{
                            backgroundColor: "#344767",
                            color: "white",
                          }}
                        >
                          {isHierarchicalReport() ? (
                            <tr>
                              <th>Account Code</th>
                              <th>Description</th>
                              <th style={{ textAlign: "center" }}>Level</th>
                              <th style={{ textAlign: "center" }}>Type</th>
                              <th style={{ textAlign: "right" }}>
                                Total Credit
                              </th>
                              <th style={{ textAlign: "right" }}>
                                Total Debit
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  fontWeight: "bold",
                                }}
                              >
                                Net Amount
                              </th>
                              <th style={{ textAlign: "center" }}>
                                Transactions
                              </th>
                            </tr>
                          ) : form.report_type === "summary" ? (
                            <tr>
                              <th>Category</th>
                              <th>-</th>
                              <th style={{ textAlign: "right" }}>
                                Total Amount
                              </th>
                              <th style={{ textAlign: "right" }}>-</th>
                              <th style={{ textAlign: "right" }}>-</th>
                              <th style={{ textAlign: "center" }}>MDAs</th>
                            </tr>
                          ) : (
                            <tr>
                              <th>Code</th>
                              <th>Description</th>
                              <th style={{ textAlign: "right" }}>Revenue</th>
                              <th style={{ textAlign: "right" }}>
                                Expenditure
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  fontWeight: "bold",
                                }}
                              >
                                Net Position
                              </th>
                              <th style={{ textAlign: "center" }}>
                                Transactions
                              </th>
                            </tr>
                          )}
                        </thead>
                        <tbody>
                          {filterDisplayedRows().map((item, index) =>
                            isHierarchicalReport()
                              ? renderHierarchicalRow(item, index)
                              : renderFlatRow(item, index),
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {reportData.data.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#999",
                        }}
                      >
                        <i
                          className="fas fa-inbox"
                          style={{ fontSize: "48px", marginBottom: "15px" }}
                        ></i>
                        <p>No data found for the selected filters.</p>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            )}

            {!reportData && (
              <Row className="mt-4">
                <Col md={12}>
                  <div
                    style={{
                      backgroundColor: "#e7f3ff",
                      border: "1px solid #2196F3",
                      borderRadius: "6px",
                      padding: "15px",
                      textAlign: "center",
                    }}
                  >
                    <MDTypography variant="body2" style={{ color: "#1976D2" }}>
                      <i
                        className="fas fa-info-circle"
                        style={{ marginRight: "8px" }}
                      ></i>
                      Select filters above and click "Generate Report" to view
                      budget data with expandable parent/child hierarchy.
                    </MDTypography>
                  </div>
                </Col>
              </Row>
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
